import { useRouteLoaderData } from "@remix-run/react";
import { motion } from "framer-motion";
import { Ticket } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  FeaturedBadge,
  ServiceCardSubtitle,
  ServiceInfoButton,
  ServicePriceLabel,
  getCardPrice,
  getServiceCardTexts,
} from "~/components/booking/flow/ServiceCardExtras";
import { keepAnimationOnMainThread } from "~/lib/motion";
import { cn, getCmsServiceFromList } from "~/lib/utils";
import { sortByWpServiceOrder } from "~/lib/wp-order";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";

interface ServiceDurationSelectProps {
  services: any[];
  selectedCmsService: any;
  onSelect: (duration: any) => void;
  selectedValue: any;
  isVoucherBooking?: boolean;
  totalGuests?: number;
}

const ServiceDurationSelect = ({
  services,
  selectedCmsService,
  onSelect,
  selectedValue,
  isVoucherBooking = false,
  totalGuests,
}: ServiceDurationSelectProps) => {
  const { t, i18n } = useTranslation();
  const rootData = useRouteLoaderData<any>("root");
  const { bookingCart } = useBookingCartStore();

  const getApplicableVouchersCount = (serviceCode: string) => {
    const vouchers = bookingCart?.vouchers || [];
    if (vouchers.length === 0) return 0;

    const allVouchersAreUpsellingOnly = vouchers.every(
      (v: any) => v.voucherData?.OnlyIntegration === true
    );
    if (allVouchersAreUpsellingOnly) return 0;

    return vouchers.filter((v: any) => {
      if (
        !v.voucherData?.ServicesIncludedCodes ||
        v.voucherData.ServicesIncludedCodes.length === 0
      ) {
        return true;
      }
      return v.voucherData.ServicesIncludedCodes.includes(serviceCode);
    }).length;
  };

  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-2 items-stretch mt-4 gap-4">
      {/* Durate nell'ordine dei code su WordPress, non in quello dell'API. */}
      {sortByWpServiceOrder(
        services || [],
        (service: any) => [service.Code],
        rootData.servicesData,
      ).map((service, index) => {
        const cmsService = getCmsServiceFromList(
          rootData.servicesData,
          service.Code
        );
        if (!cmsService || cmsService.id !== selectedCmsService.id) {
          return null;
        }
        const codeObj = cmsService.acf.codes?.find(
          (c: any) => c.code === service.Code
        );
        const applicableVouchersCount = getApplicableVouchersCount(
          service.Code
        );

        // Card show_anyway: SOLO se il service è un ghost iniettato da
        // ServiceTypeStep (cioè l'API non ha restituito il code). Quando
        // l'API restituisce il servizio il flag in ACF viene ignorato e
        // la card si comporta normalmente.
        const isShowAnyway = !!service?.isShowAnyway;
        const showAnywayText = service?.showAnywayText || "";

        // Disponibilità: la card è ESAURITA se il servizio espone slot di
        // availability ma nessuno ha posti (Quantity.Total > 0). Vale anche per
        // i pacchetti (Availability di primo livello del pacchetto): un
        // pacchetto a disponibilità 0 deve risultare sold out già sulla card,
        // non solo entrando negli orari.
        const allAvailabilities = Array.isArray(service.Availability)
          ? service.Availability
          : [];
        const hasAvailability =
          allAvailabilities.length > 0 &&
          allAvailabilities.some((a: any) => (a.Quantity?.Total || 0) > 0);
        const isSoldOut = !isShowAnyway && !hasAvailability;

        const displayImage = codeObj?.image?.id || cmsService?.acf?.image?.id;
        const cardTitle = codeObj?.short_description || cmsService?.acf?.title;
        const { subtitle: cardSubtitle, description: cardDescription } =
          getServiceCardTexts(cmsService, service.Code);

        return (
          <div
            key={index}
            className={cn(
              "w-full h-full bg-white shadow-md rounded-2xl overflow-hidden relative flex flex-col",
              {
                "ring-2 ring-secondary": selectedValue?.Code === service.Code,
                "cursor-pointer": !isShowAnyway && !isSoldOut,
                "cursor-not-allowed opacity-60": isShowAnyway || isSoldOut,
              }
            )}
            onClick={() => {
              if (isShowAnyway || isSoldOut) return;
              onSelect(service);
            }}
          >
            <ServiceInfoButton
              title={cardTitle}
              description={cardDescription}
              imageId={displayImage}
            />
            {displayImage && (
              <div className="w-full h-52 relative shrink-0">
                <motion.img
                  onUpdate={keepAnimationOnMainThread}
                  alt=""
                  src={`/cms-asset/${displayImage}`}
                  className={cn(
                    "w-full h-full object-cover object-center z-10",
                    { "opacity-50": isSoldOut }
                  )}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1 + 0.5,
                    ease: "easeInOut",
                  }}
                />
                <FeaturedBadge
                  cmsService={cmsService}
                  code={service.Code}
                  onImage
                  className="absolute bottom-0 -right-0.5 z-20"
                />
                {applicableVouchersCount > 0 &&
                  isVoucherBooking &&
                  !isShowAnyway && (
                    <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-secondary text-white rounded-full px-2 py-1">
                      <Ticket size={14} />
                      <span className="text-xs font-semibold">
                        {applicableVouchersCount}
                      </span>
                    </div>
                  )}
                {isShowAnyway && showAnywayText && (
                  <div className="absolute inset-0 z-30 bg-black bg-opacity-60 flex items-center justify-center p-4">
                    <span className="text-white font-semibold text-center text-base">
                      {showAnywayText}
                    </span>
                  </div>
                )}
                {isSoldOut && (
                  <div className="absolute inset-0 z-30 bg-black bg-opacity-50 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {t("SOLD OUT")}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="p-4 flex flex-row items-center justify-between relative flex-1">
              {isShowAnyway && !displayImage && showAnywayText && (
                <div className="absolute inset-0 z-30 bg-black bg-opacity-60 flex items-center justify-center p-3 rounded-2xl">
                  <span className="text-white font-semibold text-center text-sm">
                    {showAnywayText}
                  </span>
                </div>
              )}
              <div
                className={cn("flex flex-col flex-1", {
                  "pl-10": !displayImage && !!cardDescription,
                })}
              >
                <div className="flex flex-col">
                  {!displayImage && (
                    <FeaturedBadge cmsService={cmsService} code={service.Code} />
                  )}
                  {!codeObj.short_description && (
                    <span>{cmsService?.acf?.title}</span>
                  )}
                  {codeObj.short_description && (
                    <span>{codeObj.short_description}</span>
                  )}
                  <ServiceCardSubtitle text={cardSubtitle} />
                  <ServicePriceLabel
                    cmsService={cmsService}
                    code={service.Code}
                    isPackage={service.Type === "Package"}
                    totalGuests={totalGuests}
                  />
                  {isSoldOut && (
                    <span className="text-xs font-bold text-red-600 mt-1">
                      {t("SOLD OUT")}
                    </span>
                  )}
                </div>
              </div>
              {!isShowAnyway && (
                <div className="flex flex-col items-center justify-center">
                  <div className="bg-secondary rounded-2xl">
                    <span className="text-secondary-foreground block px-2 py-0.5">
                      {(() => {
                        const basePrice =
                          service.MinPrices?.[0]?.EffectivePrice?.[0]?.Price;
                        if (basePrice === undefined || basePrice === null)
                          return null;
                      // Packages show the price per person (see getCardPrice).
                      const displayed = getCardPrice(service, basePrice);
                      return (
                        <>
                          €
                          {displayed.toLocaleString(i18n.language, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: Number.isInteger(displayed)
                              ? 0
                              : 2,
                          })}
                        </>
                      );
                    })()}
                    </span>
                  </div>
                </div>
              )}
            </div>
            {!displayImage &&
              applicableVouchersCount > 0 &&
              isVoucherBooking &&
              !isShowAnyway && (
              <div className="absolute top-2 right-2 z-20 flex items-center gap-1 bg-secondary text-white rounded-full px-2 py-1">
                <Ticket size={14} />
                <span className="text-xs font-semibold">
                  {applicableVouchersCount}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ServiceDurationSelect;
